import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface DbUser {
  id: number;
  email: string;
  name: string;
  avatarEmoji: string;
  plan: 'free' | 'pro';
  role: 'user' | 'admin';
  createdAt: number;
  lastLoginAt: number | null;
}

export function AdminUsers(): JSX.Element {
  const me = useAuth().user;
  const [users, setUsers] = useState<DbUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ users: DbUser[] }>('/api/admin/users');
      setUsers(r.users);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '加载失败');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function update(id: number, patch: { plan?: 'free' | 'pro'; role?: 'user' | 'admin' }): Promise<void> {
    setPendingId(id);
    try {
      await api('/api/admin/promote', { method: 'POST', body: { userId: id, ...patch } });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '操作失败');
    } finally {
      setPendingId(null);
    }
  }

  if (error !== null) {
    return <div className="rounded-2xl border border-ember-400/30 bg-ember-400/[0.06] p-4 text-sm text-ember-200">⚠ {error}</div>;
  }
  if (users === null) {
    return <div className="text-cream-400 text-sm font-arcade tracking-widest">LOADING…</div>;
  }

  return (
    <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
      <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
        <div className="font-display font-bold text-white">用户列表（共 {users.length}）</div>
        <button onClick={() => void load()} className="text-xs text-honey-300 hover:text-honey-200 font-arcade tracking-widest">REFRESH</button>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {users.map((u) => {
          const isMe = me?.id === u.id;
          return (
            <div key={u.id} className="px-4 sm:px-5 py-3 flex items-start sm:items-center gap-3 flex-wrap sm:flex-nowrap">
              {/* Avatar + identity — always full row on mobile */}
              <div className="flex items-center gap-3 w-full sm:w-auto sm:flex-1 min-w-0">
                <div className="text-2xl shrink-0">{u.avatarEmoji}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-medium truncate">
                    {u.name}
                    {isMe && <span className="ml-2 text-[10px] font-arcade text-mint-300 tracking-widest">YOU</span>}
                  </div>
                  <div className="text-xs text-cream-400 font-mono truncate">{u.email}</div>
                </div>
              </div>
              {/* Date column — hidden on phones (info isn't critical) */}
              <div className="text-xs text-cream-400 hidden lg:block whitespace-nowrap">
                注册 {new Date(u.createdAt).toLocaleDateString('zh-CN')}
                {u.lastLoginAt !== null && (
                  <span className="ml-2">· 上次 {new Date(u.lastLoginAt).toLocaleDateString('zh-CN')}</span>
                )}
              </div>
              {/* Badges + actions — wrap onto their own row on phones */}
              <div className="flex items-center gap-1.5 flex-wrap ml-auto sm:ml-0">
                <Badge tone={u.plan === 'pro' ? 'honey' : 'cream'}>
                  {u.plan === 'pro' ? '★ Pro' : 'Free'}
                </Badge>
                <Badge tone={u.role === 'admin' ? 'pink' : 'cream'}>
                  {u.role === 'admin' ? 'Admin' : 'User'}
                </Badge>
                {u.plan === 'free' ? (
                  <ActionBtn disabled={pendingId === u.id} onClick={() => void update(u.id, { plan: 'pro' })}>→ Pro</ActionBtn>
                ) : (
                  <ActionBtn disabled={pendingId === u.id} onClick={() => void update(u.id, { plan: 'free' })}>→ Free</ActionBtn>
                )}
                {u.role === 'user' ? (
                  <ActionBtn disabled={pendingId === u.id} onClick={() => void update(u.id, { role: 'admin' })}>→ Admin</ActionBtn>
                ) : (
                  !isMe && (
                    <ActionBtn disabled={pendingId === u.id} onClick={() => void update(u.id, { role: 'user' })}>→ User</ActionBtn>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Badge({ tone, children }: { tone: 'honey' | 'cream' | 'pink'; children: React.ReactNode }): JSX.Element {
  const c = tone === 'honey' ? 'text-honey-300 bg-honey-400/15 border-honey-400/30'
    : tone === 'pink' ? 'text-ember-300 bg-ember-400/15 border-ember-400/30'
    : 'text-cream-300 bg-white/[0.05] border-white/[0.08]';
  return <span className={`text-[10px] px-2 py-0.5 rounded border font-arcade tracking-widest ${c}`}>{children}</span>;
}

function ActionBtn({ children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  return (
    <button
      {...rest}
      className="text-[10px] font-arcade tracking-widest px-2 py-1 rounded bg-ink-900/80 border border-white/[0.08] text-cream-200 hover:bg-honey-400/20 hover:text-honey-200 hover:border-honey-400/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );
}
